import React, { ReactNode, useCallback, useMemo, useRef } from 'react';
import { FileUploadV2Style as S } from './FileUploadV2.style';
import { VStack } from 'components/layouts/primitives/Stack/Stack';
import { FileUploadList } from './components/FileUploadList/FileUploadList';
import {
  FileUploadHandlerEvent,
  FileUpload as FileUploadPrime,
  FileUploadProps,
} from 'primereact/fileupload';
import { Text } from 'components/Text/Text';
import { useClassNames } from 'hooks/useClassNames';
import { PButton, PButtonProps } from 'components/prime/PButton/PButton';

export type FileUploadError = {
  type: 'format' | 'size';
  file: File;
};

type FileUploadV2Props = Omit<FileUploadProps, 'onError' | 'maxFileSize' | 'mode' | 'chooseLabel'> & {
  label?: string;
  hideLabel?: boolean;
  value?: File[];
  description?: ReactNode;
  fileSizeLimit?: Record<string, number> | number;
  maxFiles?: number;
  invalid?: boolean;
  onChange?: (files: File[]) => void;
  onError?: (errors: FileUploadError[] | null) => void;
  hideFileUploadList?: boolean;
  hideFileUploadListEmptyState?: boolean;
  severity?: 'default' | 'compact';
  chooseButtonLabel?: string;
  chooseButtonIcon?: PButtonProps['iconLeft'];
};

export const FileUploadV2: React.FC<FileUploadV2Props> = (props) => {
  const fileComponentRef = useRef<FileUploadPrime | null>(null);
  const openBrowse = useCallback(() => fileComponentRef?.current?.getInput().click(), [fileComponentRef]);
  const {
    accept,
    value,
    emptyTemplate,
    description,
    invalid,
    disabled,
    maxFiles,
    fileSizeLimit,
    multiple,
    label,
    hideLabel,
    onChange,
    onError,
    hideFileUploadList,
    severity,
    chooseButtonLabel,
    chooseButtonIcon,
    hideFileUploadListEmptyState,
  } = props;
  const chooseLabel = chooseButtonLabel || 'Upload files';
  const chooseIcon = chooseButtonIcon || 'uploadCloud01Line';

  const acceptedExtensions = useMemo(() => {
    return accept?.split(',').map((ext) => ext.slice(1).toLowerCase()) || null;
  }, [accept]);

  const classNames = useClassNames(
    {
      invalid,
      disabled,
      [`severity-${severity || 'default'}`]: true,
    },
    props.className
  );

  const getFileSizeLimit = useCallback(
    (fileExtension: string) => {
      if (typeof fileSizeLimit === 'number') return fileSizeLimit;
      else if (typeof fileSizeLimit === 'object' && fileSizeLimit !== null)
        return fileSizeLimit[fileExtension] || 0;
      else return 0;
    },
    [fileSizeLimit]
  );

  const splitInvalidFiles = useCallback(
    (files: File[]) => {
      let validFiles: File[] = [];
      let invalidFiles: FileUploadError[] = [];
      files.forEach((file) => {
        const fileExtension = file.name.split('.').pop() || '';
        const fileSizeLimit = getFileSizeLimit(fileExtension);
        if (acceptedExtensions && !acceptedExtensions.includes(fileExtension.toLowerCase()))
          invalidFiles = [...invalidFiles, { type: 'format', file }];
        else if (fileSizeLimit && file.size >= fileSizeLimit)
          invalidFiles = [...invalidFiles, { type: 'size', file }];
        else validFiles = [...validFiles, file];
      });
      return { validFiles, invalidFiles };
    },
    [acceptedExtensions, getFileSizeLimit]
  );

  const uploadHandler = useCallback(
    (e: FileUploadHandlerEvent) => {
      // 1. Get current files
      let files: File[] = multiple ? value || [] : [];
      // 2. Filter repeated files
      const fileNames = value ? value.map((f) => f.name) : [];
      const newFiles = e.files.filter((f) => !fileNames.includes(f.name));
      files = [...files, ...newFiles];
      // 3. Validate max files
      files = typeof maxFiles === 'number' ? files.slice(0, maxFiles) : files;
      // 4. Split invalid files
      const { validFiles, invalidFiles } = splitInvalidFiles(files);
      // 5. Clean local component
      e.options.clear();
      // 6. Notify errors
      onError?.(invalidFiles.length ? invalidFiles : null);
      // 7. Set files
      onChange?.(validFiles);
    },
    [multiple, value, maxFiles, splitInvalidFiles, onError, onChange]
  );

  const cleanProps = useMemo(
    () =>
      (({
        label,
        value,
        onChange,
        description,
        maxFiles,
        invalid,
        onError,
        fileSizeLimit,
        hideLabel,
        hideFileUploadList,
        severity,
        chooseButtonLabel,
        chooseButtonIcon,
        hideFileUploadListEmptyState,
        ...o
      }: any) => o)(props),
    [props]
  );

  return (
    <S.Container className={classNames}>
      <VStack gap="gap5">
        <VStack gap="gap2">
          {!hideLabel && severity !== 'compact' && (
            <Text as="span" color="neutral400" size="fontSizeSM" lineHeight="lineHeightMD">
              {label || 'Add files'}
            </Text>
          )}
          {severity === 'compact' && (
            <PButton
              severity="tertiary"
              label={chooseLabel}
              iconLeft={chooseIcon}
              onClick={openBrowse}
              disabled={disabled}
              size="sm"
            />
          )}
          <S.FileUpload
            {...cleanProps}
            auto
            customUpload
            ref={fileComponentRef}
            mode={severity === 'compact' ? 'basic' : 'advanced'}
            emptyTemplate={
              emptyTemplate || (
                <VStack stretch gap="gap3" justify="center" align="center">
                  <PButton
                    severity="soft"
                    iconLeft={chooseIcon}
                    label={chooseLabel}
                    onClick={openBrowse}
                    disabled={disabled}
                    size="sm"
                  />
                  <VStack gap="gap1">
                    <Text
                      as="span"
                      color="neutral500"
                      size="fontSizeSM"
                      lineHeight="lineHeightMD"
                      weight="medium"
                      align="center"
                    >
                      Drag and drop or click button to select files
                    </Text>
                    {!!description && (
                      <Text
                        as="span"
                        color="neutral400"
                        size="fontSizeSM"
                        lineHeight="lineHeightMD"
                        align="center"
                      >
                        {description}
                      </Text>
                    )}
                  </VStack>
                </VStack>
              )
            }
            uploadHandler={uploadHandler}
          />
        </VStack>
        {!hideFileUploadList && (severity === 'compact' ? !!value?.length : true) && (
          <FileUploadList
            files={value || []}
            hideEmptyState={hideFileUploadListEmptyState}
            onRemoveFile={(f) => onChange?.(value?.filter((file) => file.name !== f.name) || [])}
          />
        )}
      </VStack>
    </S.Container>
  );
};
